import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Building2,
  MapPin,
  Phone,
  Calendar,
  UserCheck,
  Loader2,
  Star,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { api } from '../App';
import Chip from '../components/Chip';
import PromotionalContentDisplay from '../components/PromotionalContentDisplay';
import { PromotionalContent } from '../types/promotional-content';
import LobbyingExplorer from '../components/lobbying/LobbyingExplorer';
import type { LobbyGroupOption } from '../types/lobbying';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import { formatDate } from '../utils/dateUtils';

interface LobbyistClient {
  client_name: string;
  zip?: string;
  bill_count: number;
  first_representation_date: string;
  last_activity_date: string;
}

interface LobbyistBill {
  id: string;
  bill_id: string;
  number: string;
  title: string;
  session: string;
  position?: string;
  created_at: string;
}

interface LobbyistDetail {
  id: string;
  primary_lobbyist_id: string;
  lobbyist_name: string;
  lobbyist_last_name: string;
  lobbyist_first_name: string;
  lobbyist_middle_name?: string;
  lobbyist_firm_name?: string;
  lobbyist_address?: string;
  lobbyist_city?: string;
  lobbyist_state?: string;
  lobbyist_zip?: string;
  lobbyist_phone?: string;
  lobbyist_type: string;
  lobbyist_designation: string;
  status: string;
  registration_end_date?: string;
  run_date?: string;
  total_years_registered?: number;
  fiscal_years_list?: string;
  promotional_content?: PromotionalContent[];
  clients?: LobbyistClient[];
  bills?: LobbyistBill[];
  created_at: string;
  updated_at: string;
}

const LOBBYIST_GROUP_OPTIONS: LobbyGroupOption[] = [
  { key: 'client', label: 'Client' },
  { key: 'bill', label: 'Bill' },
  { key: 'position', label: 'Position' },
];

const LobbyistDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [lobbyist, setLobbyist] = useState<LobbyistDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchLobbyist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchLobbyist = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/lobbyists/${id}`);
      
      if (response.data.success) {
        setLobbyist(response.data.data);
      } else {
        setError('Failed to load lobbyist details');
      }
    } catch (error: any) {
      console.error('Error fetching lobbyist:', error);
      if (error.response?.status === 404) {
        setError('Lobbyist not found');
      } else {
        setError('Failed to load lobbyist details');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'current':
        return 'success';
      case 'inactive':
        return 'neutral';
      case 'suspended':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const getTypeColor = (type: string) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800'
    ];
    return colors[Math.abs(type.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % colors.length];
  };


  const parseFiscalYears = (fiscalYearsString: string) => {
    if (!fiscalYearsString) return [];
    return fiscalYearsString.split(';').map(year => year.trim()).filter(year => year);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 pt-2">
        <div className="card">
          <div className="text-center py-12">
            <div className="text-red-500 text-lg font-medium mb-4">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!lobbyist) {
    return (
      <div className="space-y-6 pt-2">
        <div className="card">
          <div className="text-center py-12">
            <div className="text-text-secondary dark:text-text-dark-secondary text-lg font-medium mb-4">Lobbyist not found</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageShell
      title={lobbyist.lobbyist_name}
      description={`Lobbyist ID: ${lobbyist.primary_lobbyist_id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1" />
        
        {/* SOS Link with timeout handling */}
        {lobbyist.primary_lobbyist_id && (
          <div className="flex flex-col gap-2">
            <a
              href={`https://www.sos.state.co.us/lobby/LobbyistSummary.do?quitButtonDestination=subjectResults&lobbyistId=${lobbyist.primary_lobbyist_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary hover:bg-neutral-200 dark:hover:bg-background-dark-quaternary focus:ring-primary-500 flex items-center gap-2"
              title="View on Colorado Secretary of State website"
              onClick={(e) => {
                // Add a small delay to help with session establishment
                e.preventDefault();
                const url = e.currentTarget.href;
                
                // Show a brief loading state
                const button = e.currentTarget;
                const originalText = button.innerHTML;
                button.innerHTML = '<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Opening...';
                
                setTimeout(() => {
                  button.innerHTML = originalText;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }, 500);
              }}
            >
              <ExternalLink className="w-4 h-4" />
              View on SOS
            </a>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Note: If the SOS site times out, please try clicking the link again.
            </p>
          </div>
        )}
      </div>

      {/* Promotional Content Section */}
      {lobbyist.promotional_content && lobbyist.promotional_content.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4 flex items-center">
            <Star className="h-5 w-5 mr-2 text-primary-600" />
            Organization Detail
          </h2>
          <PromotionalContentDisplay 
            content={lobbyist.promotional_content} 
            entityName={lobbyist.lobbyist_name}
            entityType="lobbyist"
            showOrganizationAttribution={true}
          />
        </div>
      )}

      {/* Lobbying Activity */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 px-1">
          <FileText className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            Lobbying Activity
          </h2>
        </div>
        <LobbyingExplorer
          scope={{ lobbyist_id: id }}
          defaultGroupBy="client"
          groupOptions={LOBBYIST_GROUP_OPTIONS}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="card">
            <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4">
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider mb-2">
                  Full Name
                </h3>
                <div className="space-y-1">
                  <p className="text-text-primary dark:text-text-dark-primary font-medium">
                    {lobbyist.lobbyist_name}
                  </p>
                  <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    <div>First: {lobbyist.lobbyist_first_name || '—'}</div>
                    <div>Middle: {lobbyist.lobbyist_middle_name || '—'}</div>
                    <div>Last: {lobbyist.lobbyist_last_name || '—'}</div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider mb-2">
                  Status & Type
                </h3>
                <div className="space-y-2">
                  <Chip 
                    label={lobbyist.status || 'Unknown'}
                    variant={getStatusVariant(lobbyist.status)}
                    size="sm"
                  />
                  {lobbyist.lobbyist_type && (
                    <div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(lobbyist.lobbyist_type)}`}>
                        {lobbyist.lobbyist_type}
                      </span>
                    </div>
                  )}
                  {lobbyist.lobbyist_designation && (
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
                      <UserCheck className="w-4 h-4 mr-2" />
                      {lobbyist.lobbyist_designation}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="card">
            <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4">
              Contact Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider mb-2">
                  Address
                </h3>
                <div className="space-y-2">
                  {lobbyist.lobbyist_address && (
                    <div className="flex items-start text-text-primary dark:text-text-dark-primary">
                      <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div>{lobbyist.lobbyist_address}</div>
                        <div>
                          {lobbyist.lobbyist_city && lobbyist.lobbyist_state && (
                            `${lobbyist.lobbyist_city}, ${lobbyist.lobbyist_state}`
                          )}
                          {lobbyist.lobbyist_zip && ` ${lobbyist.lobbyist_zip}`}
                        </div>
                      </div>
                    </div>
                  )}
                  {!lobbyist.lobbyist_address && (
                    <p className="text-text-tertiary dark:text-text-dark-tertiary">No address provided</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider mb-2">
                  Phone
                </h3>
                {lobbyist.lobbyist_phone ? (
                  <div className="flex items-center text-text-primary dark:text-text-dark-primary">
                    <Phone className="w-4 h-4 mr-2" />
                    <a
                      href={`tel:${lobbyist.lobbyist_phone}`}
                      className="hover:text-primary-600 dark:hover:text-primary-400"
                    >
                      {lobbyist.lobbyist_phone}
                    </a>
                  </div>
                ) : (
                  <p className="text-text-tertiary dark:text-text-dark-tertiary">No phone provided</p>
                )}
              </div>
            </div>
          </div>

          {/* Firm Information */}
          {lobbyist.lobbyist_firm_name && (
            <div className="card">
              <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4">
                Firm Information
              </h2>
              
              <div className="flex items-center text-text-primary dark:text-text-dark-primary">
                <Building2 className="w-5 h-5 mr-3" />
                <div>
                  <div className="font-medium">{lobbyist.lobbyist_firm_name}</div>
                  <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    {lobbyist.lobbyist_designation || 'Lobbying Firm'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Registration History */}
          {lobbyist.fiscal_years_list && (
            <div className="card">
              <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4">
                Registration History
              </h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <StatCard
                    label="Total Years Registered"
                    value={lobbyist.total_years_registered || 0}
                  />
                  
                  {lobbyist.registration_end_date && (
                    <div>
                      <h3 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
                        Registration End Date
                      </h3>
                      <div className="flex items-center text-text-primary dark:text-text-dark-primary">
                        <Calendar className="w-4 h-4 mr-2" />
                        {formatDate(lobbyist.registration_end_date)}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-medium text-neutral-500 dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
                    Fiscal Years
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {parseFiscalYears(lobbyist.fiscal_years_list).map((year, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"
                      >
                        {year}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="card">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Quick Stats
            </h2>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-neutral-600 dark:text-text-dark-secondary">Status</span>
                <Chip 
                  label={lobbyist.status || 'Unknown'}
                  variant={getStatusVariant(lobbyist.status)}
                  size="sm"
                />
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-neutral-600 dark:text-text-dark-secondary">Years Active</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {lobbyist.total_years_registered || 0}
                </span>
              </div>
              
              {lobbyist.lobbyist_state && (
                <div className="flex justify-between items-center">
                  <span className="text-neutral-600 dark:text-text-dark-secondary">State</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {lobbyist.lobbyist_state}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* System Information */}
          <div className="card">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              System Information
            </h2>
            
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-neutral-600 dark:text-text-dark-secondary">Primary ID:</span>
                <div className="font-mono text-neutral-900 dark:text-white">
                  {lobbyist.primary_lobbyist_id}
                </div>
              </div>
              
              <div>
                <span className="text-neutral-600 dark:text-text-dark-secondary">Record Created:</span>
                <div className="text-neutral-900 dark:text-white">
                  {formatDate(lobbyist.created_at)}
                </div>
              </div>
              
              <div>
                <span className="text-neutral-600 dark:text-text-dark-secondary">Last Updated:</span>
                <div className="text-neutral-900 dark:text-white">
                  {formatDate(lobbyist.updated_at)}
                </div>
              </div>
              
              {lobbyist.run_date && (
                <div>
                  <span className="text-neutral-600 dark:text-text-dark-secondary">Data Run Date:</span>
                  <div className="text-neutral-900 dark:text-white">
                    {formatDate(lobbyist.run_date)}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </PageShell>
  );
};

export default LobbyistDetailPage;